import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import {
  HiMenu,
  HiX,
  HiOutlineChevronDown,
  HiOutlineUserCircle,
  HiOutlineDesktopComputer,
  HiOutlineLogin, // البديل الصحيح والموجود في المكتبة
} from "react-icons/hi";
import { MdOutlineCategory } from "react-icons/md";
import { FaShoppingCart } from "react-icons/fa";
import { HiOutlineShoppingCart } from "react-icons/hi2";

export default function NavbarForHome() {
  const location = useLocation();
  const { role } = useSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false); // منيو الموبايل
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // منيو الأقسام
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  // إغلاق القوائم عند تغيير المسار
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  // إغلاق القوائم عند الضغط خارجها
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // كلاس الروابط في القائمة العلوية
  const navLinkClass = ({ isActive }) =>
    `relative px-4 py-2 text-sm font-bold transition-all duration-300 flex items-center gap-2 rounded-xl ${
      isActive
        ? "text-mainLight bg-mainLight/10 shadow-sm"
        : "text-gray-600 dark:text-gray-400 hover:text-mainLight hover:bg-white dark:hover:bg-white/5"
    }`;

  // كلاس الروابط داخل القوائم المنسدلة
  const dropdownLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-[13px] transition-all duration-300 ${
      isActive
        ? "bg-mainLight text-white font-bold rounded-2xl mx-2"
        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-2xl mx-2"
    }`;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[1000] p-4 pointer-events-none"
      dir="rtl"
    >
      <header className="container mx-auto max-w-6xl pointer-events-auto">
        {/* الحاوية الرئيسية الزجاجية */}
        <div className="bg-white/80 dark:bg-mainDark/80 backdrop-blur-xl border border-white/20 dark:border-white/5 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] rounded-[2rem] px-6 py-3 flex items-center justify-between transition-all duration-500">
          {/* Logo Section */}
          <NavLink to="/" className="flex items-center group">
            <div className="relative">
              <img
                src="/assets/images/logo.png"
                alt="Logo"
                className="h-11 w-11 group-hover:rotate-12 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-mainLight/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <span className="hidden md:block text-xl font-bold mr-3 text-gray-800 dark:text-white tracking-tighter">
              العالمية <span className="text-mainLight">للابتوب</span>
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2 bg-gray-100/50 dark:bg-black/20 p-1.5 rounded-[1.5rem] border border-gray-200/50 dark:border-white/5">
            <NavLink to="/laptops" className={navLinkClass}>
              <HiOutlineDesktopComputer size={18} /> اللابتوبات
            </NavLink>

            {/* Dropdown Menu */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`px-4 py-2 text-sm font-bold flex items-center gap-2 rounded-xl transition-all ${
                  isDropdownOpen
                    ? "text-mainLight bg-white dark:bg-white/5 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-mainLight"
                }`}
              >
                <MdOutlineCategory size={18} /> أقسام أخرى
                <HiOutlineChevronDown
                  className={`transition-transform duration-300 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-4 w-56 bg-white/95 dark:bg-[#1a1c1e]/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-gray-100 dark:border-white/10 py-3 z-[1001] animate-in fade-in slide-in-from-top-4 duration-300">
                  <NavLink to="/accessories" className={dropdownLinkClass}>
                    إكسسوارات اللابتوب
                  </NavLink>
                  <NavLink to="/console" className={dropdownLinkClass}>
                    الكونسول وملحقاته
                  </NavLink>
                  <NavLink to="/digital-services" className={dropdownLinkClass}>
                    الخدمات الرقمية
                  </NavLink>
                  <div className="my-2 border-t border-gray-100 dark:border-white/5 mx-4" />
                  <NavLink to="/desktops" className={dropdownLinkClass}>
                    الحواسيب المكتبية
                  </NavLink>
                </div>
              )}
            </div>
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <NavLink
              to={"/my-cart"}
              className="flex items-center gap-2 bg-mainLight text-white  p-2.5 rounded-2xl font-bold text-sm shadow-lg shadow-mainLight/25 hover:shadow-mainLight/40 hover:-translate-y-0.5 active:scale-95 transition-all"
            >
              <HiOutlineShoppingCart size={20} />
            </NavLink>

            <NavLink
              to={role !== "customer" ? "/login" : "/cp"}
              className="flex items-center gap-2 bg-mainLight text-white px-5 py-2.5 rounded-2xl font-bold text-sm shadow-lg shadow-mainLight/25 hover:shadow-mainLight/40 hover:-translate-y-0.5 active:scale-95 transition-all"
            >
              {role === "customer" ? (
                <HiOutlineUserCircle size={20} />
              ) : (
                <HiOutlineLogin size={20} />
              )}

              <span className="hidden sm:inline">
                {role !== "customer" ? "تسجيل الدخول" : "حسابي"}
              </span>
              <span className=" sm:hidden">
                {role !== "customer" ? "دخول" : "حسابي"}
              </span>
            </NavLink>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2.5 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:text-mainLight transition-colors"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <HiX size={24} /> : <HiMenu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Sidebar (Slide Down) */}
        {isOpen && (
          <div className="md:hidden mt-3 bg-white/90 dark:bg-mainDark/90 backdrop-blur-2xl rounded-[2.5rem] p-4 border border-white/20 shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="flex flex-col gap-2">
              <NavLink to="/laptops" className={dropdownLinkClass}>
                اللابتوبات
              </NavLink>
              <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-[1.5rem] mt-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 mr-2">
                  الأقسام الأخرى
                </p>
                <div className="grid grid-cols-1 gap-1">
                  <NavLink to="/accessories" className={dropdownLinkClass}>
                    إكسسوارات
                  </NavLink>
                  <NavLink to="/console" className={dropdownLinkClass}>
                    الكونسول
                  </NavLink>
                  <NavLink to="/digital-services" className={dropdownLinkClass}>
                    الخدمات الرقمية
                  </NavLink>
                  <NavLink to="/desktops" className={dropdownLinkClass}>
                    المكتبية
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </div>
  );
}
